import React, { useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import humanizeString from 'humanize-string';
import { ErrorBoundary } from 'react-error-boundary';

import {
  Alert,
  Card,
  Dropdown,
  DropdownMenuItem,
  GenericError,
  H3,
  Icon,
  NewButton,
  Text,
  Time
} from '../../../../../components';
import { VegaChart } from '../VegaChart';
import { AnswerTable } from '../AnswerTable';
import { SingleValueAnswer } from '../SingleValueAnswer';
import { formatCell } from '../../../utils';
import { AssetFooterSource } from '../../atoms';

import { questionShape, sourceShape } from '../../../../../propsShape';

// Loading spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinningIcon = styled(Icon)`
  animation: ${spin} 1.5s linear infinite;
`;

const AssetsCardContainer = styled(Card)`
  cursor: ${(props) => (props.$isReadOnly ? 'default' : 'pointer')};
  width: 100%;
  position: relative;

  transition: box-shadow 0.2s linear;

  &:hover {
    box-shadow: 0px 4px 14px rgba(0, 0, 0, 0.05);
  }
`;

const AssetTitle = styled(H3)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  column-gap: 24px;
`;

const AssetCardActionsDropdown = styled(Dropdown)`
  visibility: hidden;

  ${AssetsCardContainer}:hover & {
    visibility: visible;
  }

  @media (max-width: 768px) {
    visibility: visible;
  }
`;

const RefreshLabel = styled.div`
  display: flex;
  align-items: center;
  column-gap: 4px;
  padding: 8px;
  border-radius: 8px;
  background: var(--white);
  border: 1px solid var(--border-muted);
  color: var(--content-body);
`;

const SingleAssetRefreshLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
`;

const RefreshText = styled(Text)`
  font-weight: 600;
`;

const RefreshOverlay = styled.div`
  position: absolute;
  z-index: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 20px;

  @media (max-width: 768px) {
    border-radius: 16px;
  }
`;

const SourcesContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshButton = styled(NewButton)`
  border: 1px solid var(--border-muted);
`;

const AssetCard = ({
  onClick,
  onRemove,
  onRefresh,
  question = null,
  isReadOnly = false,
  sources = []
}) => {
  const rowData = useMemo(() => question.answer?.data?.rows.map((r) => r.fields), []);
  const columnDefs = useMemo(() => {
    if (!question.answer?.data?.headers) return [];

    // Get the column order from the answer, or use the default order
    const columnOrder =
      question.answer.columnOrder || question.answer.data.headers.map((h) => h.column);

    // Sort headers based on column order
    const sortedHeaders = [...question.answer.data.headers].sort((a, b) => {
      const indexA = columnOrder.indexOf(a.column);
      const indexB = columnOrder.indexOf(b.column);
      return indexA - indexB;
    });

    return sortedHeaders.map((h) => ({
      field: h.column,
      headerName: humanizeString(h.column),
      cellRenderer: (params) => formatCell(params, question.answer.formattingHints)
    }));
  }, [
    question.answer?.data?.headers,
    question.answer?.columnOrder,
    question.answer?.formattingHints
  ]);

  const handleRemove = useCallback(
    (evt) => {
      evt.stopPropagation();
      onRemove(question);
    },
    [question, onRemove]
  );

  const isChart = question.lastChartFollowUp || question.isChart;

  const filteredSources = useMemo(
    () =>
      sources?.filter((source) => {
        if (question.answer?.schemas) {
          if (
            question.answer?.schemas
              .filter((s) => s !== 'multi' && s !== 'metrics')
              .includes(source.integration.schema)
          ) {
            return true;
          }
        }
        return false;
      }),
    [question, sources]
  );

  const renderAnswer = useCallback(() => {
    if (isChart && question.lastChartFollowUp?.answer?.vegaSpec) {
      return <VegaChart spec={question.lastChartFollowUp?.answer?.vegaSpec} />;
    }
    if (question.answer?.isSingleValueAnswer || question.answer?.isMetricsSingleValueAnswer) {
      return <SingleValueAnswer question={question} />;
    }
    return (
      <>
        <Text>{question?.answer?.body}</Text>
        {question?.answer?.data && (
          <AnswerTable
            id={question.answer.id}
            rowData={rowData}
            columnDefs={columnDefs}
            pagination={question?.answer?.data.rows?.length > 5}
            pageSize={5}
            shouldShowAllRows
          />
        )}
      </>
    );
  }, [isChart, question.lastChartFollowUp?.answer?.vegaSpec]);

  return (
    <AssetsCardContainer {...(!isReadOnly && { onClick })} $isReadOnly={isReadOnly}>
      <ErrorBoundary fallback={<GenericError />}>
        {question.hasFailedNewVersions && (
          <Alert
            icon={<Icon color='var(--content-body)' name='alert' />}
            message={
              <>
                Data refresh failed. This is a previous version from{' '}
                <Time time={question.createdAt} format='MMM DD, YY hh:mm A' />
              </>
            }
            actions={
              <RefreshButton
                secondary
                onClick={(evt) => {
                  evt.stopPropagation();
                  onRefresh(question.versions.find((v) => v.isLatest).id || question.id);
                }}
              >
                Refresh
              </RefreshButton>
            }
          />
        )}
        {question.isRefreshing && (
          <RefreshOverlay>
            <RefreshLabel>
              <Icon size={20} name='refresh' />
              <RefreshText>Refreshing data</RefreshText>
            </RefreshLabel>
          </RefreshOverlay>
        )}
        {!question.answer && (
          <RefreshOverlay>
            <SingleAssetRefreshLabel>
              <SpinningIcon name='steps-loader' size={16} displaySize={20} />
              <RefreshText>Refreshing data</RefreshText>
            </SingleAssetRefreshLabel>
          </RefreshOverlay>
        )}
        <AssetTitle>
          {question.assetTitle || question.body}
          {!isReadOnly && (
            <AssetCardActionsDropdown
              menuPlacement='bottom-end'
              menuStyle={{ width: 200 }}
              toggleComponent={
                <NewButton secondary>
                  <Icon name='ellipsis' size={20} />
                </NewButton>
              }
            >
              {() => (
                <DropdownMenuItem
                  leftIcon={<Icon displaySize={20} name='trash' />}
                  onClick={handleRemove}
                >
                  Remove
                </DropdownMenuItem>
              )}
            </AssetCardActionsDropdown>
          )}
        </AssetTitle>

        {renderAnswer()}
        {filteredSources.length > 0 && (
          <SourcesContainer>
            {filteredSources.map((source, index) => (
              <AssetFooterSource
                key={source.id}
                source={source}
                showPlus={index !== filteredSources.length - 1}
              />
            ))}
          </SourcesContainer>
        )}
      </ErrorBoundary>
    </AssetsCardContainer>
  );
};

AssetCard.propTypes = {
  question: questionShape,
  onRemove: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired,
  onRefresh: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  sources: PropTypes.arrayOf(sourceShape)
};

export { AssetCard };
